// src/pages/MasterUsers.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "../services/api";
import { useNavigate } from "react-router-dom";
import PageShell from "../components/PageShell";

// Allowed target roles (cannot set MASTER via UI)
const ROLE_OPTIONS = ["USER", "EVENT_WORKER", "ADMIN", "AFFILIATE"];

function useDebounced(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function MasterUsers() {
  const navigate = useNavigate();

  // current admin (to prevent self-demotion in UI)
  const [me, setMe] = useState(null);

  // list state
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0); // 0-based
  const [size, setSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);

  // sorting
  const [sortField, setSortField] = useState("id");
  const [sortDir, setSortDir] = useState("DESC");

  // search
  const [emailQuery, setEmailQuery] = useState("");
  const debouncedEmail = useDebounced(emailQuery, 500);

  // per-row edits
  const [editRole, setEditRole] = useState({}); // { [userId]: "ROLE" }
  const [saving, setSaving] = useState({}); // { [userId]: true/false }
  const [error, setError] = useState("");

  // delete modal state
  const [deleteUser, setDeleteUser] = useState(null); // row to delete
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const sortParam = useMemo(() => `${sortField},${sortDir}`, [sortField, sortDir]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/api/account/me");
        setMe(data);
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, sortParam, debouncedEmail]);

  const loadUsers = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.get("/api/master/users", {
        params: {
          page,
          size,
          sort: sortParam,
          email: debouncedEmail || undefined,
        },
      });
      setItems(data.content || []);
      setTotalPages(data.totalPages ?? 0);
      setTotalElements(data.totalElements ?? 0);
    } catch (e) {
      const msg =
        e?.response?.status === 403
          ? "You must be a MASTER to view this page."
          : e?.response?.data?.message || "Failed to load users.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortField(field);
      setSortDir("ASC");
    }
    setPage(0);
  };

  const onChangeRole = (userId, value) => {
    setEditRole((prev) => ({ ...prev, [userId]: value }));
  };

  const applyRole = async (row) => {
    const newRole = editRole[row.id];
    if (!newRole || newRole === row.role) return;

    setSaving((s) => ({ ...s, [row.id]: true }));
    try {
      await axios.patch(`/api/master/users/${row.id}/role`, { role: newRole });
      setItems((prev) => prev.map((u) => (u.id === row.id ? { ...u, role: newRole } : u)));
      setEditRole((prev) => {
        const copy = { ...prev };
        delete copy[row.id];
        return copy;
      });
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to update role.");
    } finally {
      setSaving((s) => ({ ...s, [row.id]: false }));
    }
  };

  const canEdit = (row) => {
    const isMasterTarget = row.role === "MASTER";
    const isSelf = me && (me.id === row.id || me.email === row.email);
    return !isMasterTarget && !isSelf;
  };

  const openDelete = (row) => {
    setDeleteUser(row);
    setDeleteReason("");
  };

  const confirmDelete = async () => {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      await axios.post(`/api/master/users/${deleteUser.id}/anonymise`, {
        reason: deleteReason || ""
      });
      // reflect in table
      setItems((prev) =>
        prev.map((u) =>
          u.id === deleteUser.id ? { ...u, deleted: true, subscriptionActive: false } : u
        )
      );
      setDeleteUser(null);
      setDeleteReason("");
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to anonymise user.");
    } finally {
      setDeleting(false);
    }
  };

  /* ---------- UI ---------- */

  return (
    <PageShell>
      <div className="page space-y-6">
        {/* Header */}
        <section className="card">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-3xl md:text-4xl">Master • Users</h1>
              <p className="mt-1 text-slate-300">
                Manage roles, sort & filter, and page through all users.
              </p>
            </div>
            <button onClick={() => navigate(-1)} className="btn-secondary">← Back</button>
          </div>

          {/* Filters */}
          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                Search by email
              </label>
              <input
                type="text"
                value={emailQuery}
                onChange={(e) => {
                  setEmailQuery(e.target.value);
                  setPage(0);
                }}
                placeholder="e.g. user@example.com"
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                Page size
              </label>
              <select
                value={size}
                onChange={(e) => {
                  setSize(Number(e.target.value));
                  setPage(0);
                }}
                className="input"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n} className="bg-[var(--surface)] text-slate-100">
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:self-end">
              <div className="text-sm text-slate-400">
                Showing page <span className="text-slate-200 font-medium">{page + 1}</span> of{" "}
                <span className="text-slate-200 font-medium">{Math.max(totalPages, 1)}</span> • Total{" "}
                <span className="text-slate-200 font-medium">{totalElements}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Error */}
        {error && (
          <section className="card bg-red-500/10 border-red-500/20 text-red-100">{error}</section>
        )}

        {/* Table */}
        <section className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-white/[0.04] text-slate-300 sticky top-0 z-10">
                <tr>
                  <Th label="ID" field="id" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Email" field="email" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Username" field="username" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Role" field="role" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th
                    label="Sub Active"
                    field="subscriptionActive"
                    sortField={sortField}
                    sortDir={sortDir}
                    onSort={toggleSort}
                  />
                  <Th
                    label="Tier"
                    field="subscriptionTier"
                    sortField={sortField}
                    sortDir={sortDir}
                    onSort={toggleSort}
                  />
                  <Th label="Deleted" field="deleted" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <th className="px-3 py-2 text-left text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Loading skeleton */}
                {loading &&
                  [...Array(6)].map((_, i) => (
                    <tr key={i} className="border-t border-white/10 animate-pulse">
                      {[...Array(8)].map((__, j) => (
                        <td key={j} className="px-3 py-2">
                          <div className="h-4 w-24 bg-white/5 rounded" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!loading &&
                  items.map((row) => {
                    const pendingRole = editRole[row.id] ?? row.role;
                    const dirty = pendingRole !== row.role;
                    const disabled = !canEdit(row);

                    return (
                      <tr key={row.id} className="border-t border-white/10 hover:bg-white/[0.02]">
                        {/* ID */}
                        <td className="px-3 py-2 text-slate-200">{row.id}</td>

                        {/* Email */}
                        <td className="px-3 py-2">
                          <div className="truncate text-slate-200">{row.email}</div>
                        </td>

                        {/* Username */}
                        <td className="px-3 py-2 text-slate-200">{row.username}</td>

                        {/* Role (select) */}
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <select
                              value={pendingRole}
                              onChange={(e) => onChangeRole(row.id, e.target.value)}
                              disabled={disabled}
                              className={`rounded-lg border border-white/10 bg-white/[0.06] px-2 py-1 text-slate-100 ${disabled ? "opacity-60" : "hover:bg-white/[0.08]"
                                }`}
                            >
                              {[row.role, ...ROLE_OPTIONS]
                                .filter((v, i, arr) => arr.indexOf(v) === i)
                                .map((r) => (
                                  <option key={r} value={r} className="bg-[var(--surface)] text-slate-100">
                                    {r}
                                  </option>
                                ))}
                            </select>
                            {row.role === "MASTER" && (
                              <span className="badge bg-white/10 text-slate-300">locked</span>
                            )}
                            {me && me.id === row.id && (
                              <span className="badge bg-white/10 text-slate-300">you</span>
                            )}
                          </div>
                        </td>

                        {/* Sub Active */}
                        <td className="px-3 py-2">
                          {row.subscriptionActive ? (
                            <span className="badge bg-emerald-500/20 text-emerald-200">Active</span>
                          ) : (
                            <span className="badge bg-white/10 text-slate-300">Inactive</span>
                          )}
                        </td>

                        {/* Tier */}
                        <td className="px-3 py-2 text-slate-200">{row.subscriptionTier || "—"}</td>

                        {/* Deleted */}
                        <td className="px-3 py-2">
                          {row.deleted ? (
                            <span className="badge bg-red-500/20 text-red-200">Yes</span>
                          ) : (
                            <span className="badge bg-white/10 text-slate-300">No</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => applyRole(row)}
                              disabled={!dirty || disabled || saving[row.id]}
                              className={`px-3 py-1.5 rounded-lg ${!dirty || disabled
                                  ? "bg-white/10 text-slate-400 cursor-not-allowed"
                                  : "btn-primary"
                                }`}
                            >
                              {saving[row.id] ? "Saving…" : "Apply"}
                            </button>

                            <button
                              onClick={() => openDelete(row)}
                              disabled={disabled || row.deleted}
                              className={`px-3 py-1.5 rounded-lg ${disabled || row.deleted
                                  ? "bg-white/10 text-slate-400 cursor-not-allowed"
                                  : "bg-red-600 text-white hover:bg-red-700"
                                }`}
                              title={row.deleted ? "Already deleted" : "Delete (anonymise) account"}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                {!loading && items.length === 0 && (
                  <tr>
                    <td className="px-3 py-6 text-center text-slate-300" colSpan={8}>
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-white/10 px-3 py-2 text-sm">
            <div className="text-slate-400">
              Page <span className="text-slate-200">{page + 1}</span> of{" "}
              <span className="text-slate-200">{Math.max(totalPages, 1)}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(0)}
                disabled={page <= 0}
                className="btn-secondary disabled:opacity-50"
              >
                « First
              </button>
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page <= 0}
                className="btn-secondary disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                disabled={page + 1 >= totalPages}
                className="btn-secondary disabled:opacity-50"
              >
                Next
              </button>
              <button
                onClick={() => setPage(Math.max(totalPages - 1, 0))}
                disabled={page + 1 >= totalPages}
                className="btn-secondary disabled:opacity-50"
              >
                Last »
              </button>
            </div>
          </div>
        </section>

        {/* Delete confirmation modal */}
        {deleteUser && (
          <div
            className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
            role="dialog"
            aria-modal="true"
          >
            <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[var(--surface)] shadow-card">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-white font-semibold">
                  Delete account for {deleteUser.email}
                </h3>
                <p className="text-slate-300 text-sm mt-1">
                  This will anonymise the user’s personal data, deactivate their subscription,
                  and keep their upcoming tickets. A confirmation email will be sent to the user
                  including your reason.
                </p>
              </div>

              <div className="p-4 space-y-2">
                <label className="block">
                  <div className="label">Reason (sent to user)</div>
                  <textarea
                    rows={4}
                    className="input w-full"
                    placeholder="e.g., Repeated ToS violations"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                  />
                </label>
              </div>

              <div className="p-4 border-t border-white/10 flex items-center justify-end gap-2">
                <button
                  className="btn-secondary"
                  onClick={() => setDeleteUser(null)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="bg-red-600 text-white hover:bg-red-700 rounded-lg px-4 py-2"
                  onClick={confirmDelete}
                  disabled={deleting}
                >
                  {deleting ? "Deleting…" : "Delete & Notify"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}

function Th({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <th
      className="px-3 py-2 text-left select-none cursor-pointer text-slate-300"
      onClick={() => onSort(field)}
      title="Click to sort"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <span className="text-xs">{sortDir === "ASC" ? "▲" : "▼"}</span>}
      </span>
    </th>
  );
}
